import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from "axios";

const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache", 
      Expires: "0",
      "Content-Type": "application/json",
  
      "X-Content-Type-Options": "nosniff",
      "X-Frame-Options": "DENY", 
      "X-XSS-Protection": "1; mode=block",
      "Referrer-Policy": "strict-origin-when-cross-origin",
    },
    timeout: 30000, 
    validateStatus: (status) => status >= 200 && status < 300,
  });

  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );


  let isRefreshing = false;
  let refreshSubscribers: Array<(token: string | null) => void> = [];

  const onRefreshed = (token: string | null) => {
    refreshSubscribers.forEach((cb) => cb(token));
    refreshSubscribers = [];
  };

  const addRefreshSubscriber = (cb: (token: string | null) => void) => {
    refreshSubscribers.push(cb);
  };

  const performRefresh = async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      return null;
    }

    try {
      // VITE_AUTH_API_URL already ends in /api - do not repeat it here.
      const refreshUrl = `${import.meta.env.VITE_AUTH_API_URL}/Auth/RefreshToken`;
      const response = await axios.post(refreshUrl, { refreshToken }, {
        headers: { "Content-Type": "application/json", accept: "*/*" },
        validateStatus: (s) => s >= 200 && s < 300,
      });

      const { accessToken, idToken, refreshToken: newRefreshToken } = response.data || {};
      if (accessToken) {
        localStorage.setItem("token", accessToken);
      }
      if (idToken) {
        localStorage.setItem("idToken", idToken);
      }
      if (newRefreshToken) {
        localStorage.setItem("refreshToken", newRefreshToken);
      }
      return accessToken || null;
    } catch {
      return null;
    }
  };

  const handleUnauthorized = () => {
    const currentPath = window.location.pathname + window.location.search;
    localStorage.setItem("intendedPath", currentPath);
    localStorage.removeItem("token");
    localStorage.removeItem("idToken");
    localStorage.removeItem("refreshToken");

    const isRegistered = localStorage.getItem("auth-store")
      ? JSON.parse(localStorage.getItem("auth-store") || "{}").state?.isRegistred
      : false;

    if (!isRegistered) {
      window.location.href = "/auth?mode=signup&tokenExpired=true";
    } else {
      window.location.href = "/auth?mode=signin&tokenExpired=true";
    }
  };

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean });

      if (error.response?.status !== 401) {
        return Promise.reject(error);
      }

 
      if (originalRequest?._retry) {
        handleUnauthorized();
        return Promise.reject(error);
      }

  
      if (!isRefreshing) {
        isRefreshing = true;
        originalRequest._retry = true;
        const newToken = await performRefresh();
        isRefreshing = false;
        onRefreshed(newToken);

        if (newToken) {
         
          instance.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
          if (originalRequest.headers) {
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          }
          return instance(originalRequest);
        }

        handleUnauthorized();
        return Promise.reject(error);
      }

 
      return new Promise((resolve, reject) => {
        addRefreshSubscriber((token) => {
          if (!token) {
            handleUnauthorized();
            reject(error);
            return;
          }
          if (originalRequest.headers) {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
          }
          originalRequest._retry = true;
          resolve(instance(originalRequest));
        });
      });
    }
  );
  return instance;
};
export const authApi = createAxiosInstance(
  import.meta.env.VITE_AUTH_API_URL as string
);
export const appointmentApi = createAxiosInstance(
  import.meta.env.VITE_APPOINTMENT_API_URL as string
);
